import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Select,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast
} from "@chakra-ui/react";
import {
  FaBell,
  FaExclamationTriangle,
  FaFire,
  FaRobot,
  FaSearch,
  FaSyncAlt
} from "react-icons/fa";
import { api } from "../api";

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const isPanicTipo = (value) => normalize(value).includes("panico");
const isIaTipo = (evento) =>
  normalize(evento?.origem) === "ia" || normalize(evento?.tipo).includes("violacao");

const isEncerrado = (evento) => {
  const status = normalize(evento?.status);
  return ["encerrado", "encerrada", "resolvido", "resolvida"].includes(status);
};

const parseEventDate = (evento) => {
  const raw = evento?.detected_at || evento?.created_at || evento?.data_hora || evento?.data || null;
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatEventTime = (evento) => {
  const parsed = parseEventDate(evento);
  if (!parsed) return "--";
  return parsed.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const buildImageUrl = (apiBase, url) => {
  if (!url) return null;
  if (String(url).startsWith("http")) return url;
  return `${apiBase}${url}`;
};

const getEventSource = (evento) => {
  if (isIaTipo(evento)) return "ia";
  if (isPanicTipo(evento?.tipo)) return "panic";
  return "ocorrencia";
};

const getEventKey = (evento) => `${getEventSource(evento)}-${String(evento?.id || "sem-id")}`;

const getTipoVisual = (evento) => {
  if (isIaTipo(evento)) {
    return { label: evento?.tipo || "Violação IA", icon: FaRobot, tone: "#d6bcfa", scheme: "purple" };
  }
  if (isPanicTipo(evento?.tipo)) {
    return { label: "Pânico", icon: FaBell, tone: "#feb2b2", scheme: "red" };
  }
  if (normalize(evento?.tipo).includes("incendio")) {
    return { label: "Incêndio", icon: FaFire, tone: "#f6ad55", scheme: "orange" };
  }
  return {
    label: evento?.tipo || "Ocorrência",
    icon: FaExclamationTriangle,
    tone: "#f6ad55",
    scheme: "orange"
  };
};

const sortEventsDesc = (eventos) =>
  [...eventos].sort((a, b) => {
    const dateA = parseEventDate(a)?.getTime() || 0;
    const dateB = parseEventDate(b)?.getTime() || 0;
    if (dateA !== dateB) return dateB - dateA;
    return Number(b?.id || 0) - Number(a?.id || 0);
  });

const parseLocalUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    return {};
  }
};

const Ocorrencias = () => {
  const [eventos, setEventos] = useState([]);
  const [usuarioNomeMap, setUsuarioNomeMap] = useState({});
  const [busca, setBusca] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("ativos");
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [resolvingId, setResolvingId] = useState(null);
  const toast = useToast();

  const localUser = useMemo(() => parseLocalUser(), []);
  const currentUserId = localUser?.id ? Number(localUser.id) : null;
  const currentUserRole = normalize(localUser?.role || localUser?.tipo || "");
  const apiBase = api.defaults.baseURL?.replace(/\/api$/, "") || "";

  const loadEventos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/v2/mapa");
      const payload = response?.data || {};
      const usuarios = Array.isArray(payload.usuarios) ? payload.usuarios : [];
      const ocorrencias = Array.isArray(payload.ocorrencias) ? payload.ocorrencias : [];
      const aiEvents = Array.isArray(payload.ai_events) ? payload.ai_events : [];

      const nomeMap = {};
      usuarios.forEach((user) => {
        const id = Number(user?.usuario_id || user?.id);
        if (!id) return;
        nomeMap[id] = user?.nome || user?.usuario_nome || `Usuário ${id}`;
      });

      const baseOcorrencias = ocorrencias.map((evento) => ({
        ...evento,
        origem: evento?.origem || (isPanicTipo(evento?.tipo) ? "panic" : "ocorrencia")
      }));

      const eventosIa = aiEvents.map((evento) => ({
        ...evento,
        origem: "ia",
        tipo: evento?.tipo || "Violação IA",
        descricao: evento?.descricao || "Evento de IA detectado"
      }));

      const mergedEventos = [...baseOcorrencias, ...eventosIa];
      mergedEventos.forEach((evento) => {
        const id = Number(evento?.usuario_id);
        if (!id || nomeMap[id]) return;
        if (evento?.usuario) {
          nomeMap[id] = evento.usuario;
        }
      });

      setUsuarioNomeMap(nomeMap);
      setEventos(sortEventsDesc(mergedEventos));
    } catch (error) {
      toast({
        title: "Erro ao carregar eventos",
        description: "Não foi possível carregar a central agora.",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadEventos();
  }, [loadEventos]);

  const panicEventos = useMemo(
    () => eventos.filter((evento) => getEventSource(evento) === "panic"),
    [eventos]
  );
  const ocorrenciaEventos = useMemo(
    () => eventos.filter((evento) => getEventSource(evento) === "ocorrencia"),
    [eventos]
  );
  const iaEventos = useMemo(
    () => eventos.filter((evento) => getEventSource(evento) === "ia"),
    [eventos]
  );

  const tabs = useMemo(
    () => [
      {
        id: "panic",
        label: "Pânico",
        icon: FaBell,
        events: panicEventos,
        emptyLabel: "Nenhum alerta de pânico encontrado."
      },
      {
        id: "ocorrencia",
        label: "Ocorrências",
        icon: FaExclamationTriangle,
        events: ocorrenciaEventos,
        emptyLabel: "Nenhuma ocorrência encontrada."
      },
      {
        id: "ia",
        label: "IA",
        icon: FaRobot,
        events: iaEventos,
        emptyLabel: "Nenhum evento de IA encontrado."
      }
    ],
    [iaEventos, ocorrenciaEventos, panicEventos]
  );

  const applyFilters = useCallback(
    (lista) =>
      lista
        .filter((evento) => {
          if (statusFiltro === "ativos") return !isEncerrado(evento);
          if (statusFiltro === "encerrados") return isEncerrado(evento);
          return true;
        })
        .filter((evento) => {
          if (!busca.trim()) return true;
          const userId = Number(evento?.usuario_id);
          const nome = usuarioNomeMap[userId] || evento?.usuario || "";
          const texto = `${nome} ${evento?.tipo || ""} ${evento?.descricao || ""} ${evento?.camera_id || ""}`;
          return normalize(texto).includes(normalize(busca));
        }),
    [busca, statusFiltro, usuarioNomeMap]
  );

  const canResolve = useCallback(
    (evento) => {
      if (!evento || isEncerrado(evento)) return false;
      if (["owner", "admin", "superadmin"].includes(currentUserRole)) return true;
      return currentUserId !== null && Number(evento?.usuario_id) === currentUserId;
    },
    [currentUserId, currentUserRole]
  );

  const resolveEvento = useCallback(
    async (evento) => {
      if (!evento?.id) return;
      const source = getEventSource(evento);
      const eventKey = getEventKey(evento);
      setResolvingId(eventKey);
      try {
        if (source === "ia") {
          await api.post("/v2/ai/events/resolve", { id: evento.id });
        } else if (source === "panic") {
          await api.post("/panic/resolve", { id_alerta: evento.id });
        } else {
          await api.post("/ocorrencias/resolve", { id: evento.id });
        }

        toast({
          title: "Evento encerrado",
          status: "success",
          duration: 2200,
          isClosable: true
        });
        await loadEventos();
      } catch (error) {
        toast({
          title: "Erro ao encerrar evento",
          status: "error",
          duration: 3000,
          isClosable: true
        });
      } finally {
        setResolvingId(null);
      }
    },
    [loadEventos, toast]
  );

  const renderLista = (lista, emptyLabel) => {
    const eventosFiltrados = applyFilters(lista);
    if (loading) {
      return (
        <Box className="events-empty-state">
          <Text>Carregando eventos...</Text>
        </Box>
      );
    }
    if (!eventosFiltrados.length) {
      return (
        <Box className="events-empty-state">
          <Text>{emptyLabel}</Text>
        </Box>
      );
    }

    return (
      <Stack spacing={3} className="events-list-stack">
        {eventosFiltrados.map((evento) => {
          const tipo = getTipoVisual(evento);
          const Icon = tipo.icon;
          const source = getEventSource(evento);
          const eventKey = getEventKey(evento);
          const userId = Number(evento?.usuario_id);
          const nomeUsuario = usuarioNomeMap[userId] || evento?.usuario || `Usuário ${evento?.usuario_id || "--"}`;
          const imageUrl = buildImageUrl(apiBase, evento?.image_url);
          const confidence =
            typeof evento?.confidence === "number"
              ? `${Math.round(evento.confidence * 100)}%`
              : null;

          return (
            <Box key={eventKey} className={`events-card events-card-${source}`}>
              <HStack justify="space-between" align="start" spacing={3}>
                <HStack spacing={3} minW={0} align="center">
                  <Avatar
                    size="sm"
                    name={nomeUsuario}
                    src={userId ? `${apiBase}/usuarios/${userId}.png` : undefined}
                    className="events-avatar"
                  />
                  <Stack spacing={0} minW={0}>
                    <Text className="events-user-name" noOfLines={1}>
                      {nomeUsuario}
                    </Text>
                    <Text className="events-meta" noOfLines={1}>
                      Cam. {evento?.camera_id || "--"} • {formatEventTime(evento)}
                    </Text>
                  </Stack>
                </HStack>
                <Badge
                  borderRadius="full"
                  px={2}
                  className={`events-status-badge ${isEncerrado(evento) ? "is-closed" : "is-open"}`}
                >
                  {isEncerrado(evento) ? "Encerrado" : "Ativo"}
                </Badge>
              </HStack>

              <HStack spacing={2} mt={2} minW={0}>
                <Box color={tipo.tone}>
                  <Icon />
                </Box>
                <Text className="events-type-label" noOfLines={1}>
                  {tipo.label}
                </Text>
                {confidence ? (
                  <Badge colorScheme={tipo.scheme} borderRadius="full" px={2} fontSize="10px">
                    IA {confidence}
                  </Badge>
                ) : null}
              </HStack>

              {evento?.descricao ? (
                <Text className="events-description" mt={1}>
                  {evento.descricao}
                </Text>
              ) : null}

              {imageUrl ? (
                <Box mt={2}>
                  <img
                    src={imageUrl}
                    alt={tipo.label}
                    style={{
                      width: "100%",
                      maxHeight: "170px",
                      objectFit: "cover",
                      borderRadius: "12px",
                      border: "1px solid rgba(192, 255, 244, 0.28)"
                    }}
                  />
                </Box>
              ) : null}

              {canResolve(evento) ? (
                <Button
                  mt={3}
                  size="sm"
                  className="events-resolve-btn"
                  isLoading={resolvingId === eventKey}
                  onClick={() => resolveEvento(evento)}
                >
                  Encerrar evento
                </Button>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box px={{ base: 3, md: 5 }} py={{ base: 3, md: 4 }} className="events-page">
      <Box className="events-shell">
        <Stack spacing={4}>
          <Box className="events-header">
            <HStack justify="space-between" align="center" spacing={3} flexWrap="wrap">
              <Stack spacing={0.5}>
                <Text className="events-title">Central de Eventos</Text>
                <Text className="events-subtitle">
                  Histórico organizado por pânico, ocorrências e IA
                </Text>
              </Stack>
              <Button
                leftIcon={<FaSyncAlt />}
                onClick={loadEventos}
                isLoading={loading}
                size="sm"
                variant="outline"
                borderRadius="full"
                className="events-refresh-btn"
              >
                Atualizar
              </Button>
            </HStack>

            <Box className="events-filter-row" mt={3}>
              <Input
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                placeholder="Buscar por usuário, tipo, câmera..."
                className="events-search-input"
                pl="38px"
              />
              <Box position="relative">
                <Select
                  value={statusFiltro}
                  onChange={(event) => setStatusFiltro(event.target.value)}
                  className="events-status-select"
                >
                  <option value="ativos">Somente ativos</option>
                  <option value="encerrados">Somente encerrados</option>
                  <option value="todos">Todos</option>
                </Select>
              </Box>
              <Box className="events-search-icon">
                <FaSearch />
              </Box>
            </Box>
          </Box>

          <Tabs
            variant="unstyled"
            isLazy
            index={tabIndex}
            onChange={(index) => setTabIndex(index)}
            className="events-tabs"
          >
            <TabList className="events-tab-list">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <Tab
                    key={tab.id}
                    className="events-tab"
                    _selected={{
                      bg: "rgba(255,255,255,0.18)",
                      color: "#f6fffb",
                      borderColor: "rgba(201, 255, 242, 0.58)",
                      boxShadow: "0 8px 20px rgba(0, 12, 20, 0.2)"
                    }}
                  >
                    <HStack spacing={1.5} justify="center" minW={0}>
                      <Icon />
                      <Text className="events-tab-text" noOfLines={1}>
                        {tab.label}
                      </Text>
                      <Badge borderRadius="full" className={`events-tab-badge events-tab-badge-${tab.id}`}>
                        {tab.events.length}
                      </Badge>
                    </HStack>
                  </Tab>
                );
              })}
            </TabList>

            <TabPanels>
              {tabs.map((tab) => (
                <TabPanel key={`panel-${tab.id}`} px={0} pt={3}>
                  <Box className="events-panel-scroll">{renderLista(tab.events, tab.emptyLabel)}</Box>
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Stack>
      </Box>
    </Box>
  );
};

export default Ocorrencias;


